/**
 * "Continue with <provider>" buttons, one per enabled social provider.
 *
 * Which providers are offered comes from `ENABLED_SOCIAL_PROVIDERS` in
 * `./providers` -- the single place that decision is made, shared with the
 * Connected Accounts settings surface.
 *
 * Delegates to the platform-resolved social sign-in via useAuth. On register the
 * invite code is passed through (and the buttons are disabled until it is valid,
 * enforced by the parent screen); on login no invite travels and an uninvited
 * new user gets the invite-only message surfaced here.
 *
 * Google renders through `GoogleSignInButton`, which follows Google's sign-in
 * branding rules. Apple still uses the neutral outline button with its glyph;
 * re-enabling it needs Apple's own button guidelines applied first (on iOS,
 * the native `AppleAuthenticationButton`).
 */

import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/use-auth';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useState } from 'react';
import { View } from 'react-native';
import { GoogleSignInButton } from './google-sign-in-button';
import { ENABLED_SOCIAL_PROVIDERS, SOCIAL_PROVIDER_META } from './providers';
import type { SignInSocialFn, SocialProvider } from './sign-in-social.types';

interface SocialSignInButtonsProps {
  /** Invite code to carry into the register flow. Omitted on login. */
  inviteCode?: string;
  /** Disable every button (e.g. register: until a valid invite is entered). */
  disabled?: boolean;
  /** Which screen the buttons are on -- drives copy/testIDs. */
  context: 'register' | 'login';
}

export function SocialSignInButtons({ inviteCode, disabled, context }: SocialSignInButtonsProps) {
  const { signInWithGoogle, signInWithApple } = useAuth();
  const theme = useAppTheme();
  const [pending, setPending] = useState<SocialProvider | null>(null);

  const signInFor: Record<SocialProvider, SignInSocialFn> = {
    google: signInWithGoogle,
    apple: signInWithApple,
  };

  const handlePress = async (provider: SocialProvider) => {
    if (disabled || pending) return;
    setPending(provider);
    try {
      await signInFor[provider](inviteCode ? { inviteCode } : undefined);
      // Web: a redirect is now in flight; keep the button busy. Native (Phase 5)
      // resolves in-process and the gate/route change takes over.
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Please try again.';
      toast.error('Could not sign you in', { description: message });
      setPending(null);
    }
  };

  return (
    <View className="gap-3">
      {ENABLED_SOCIAL_PROVIDERS.map((provider) => {
        const { signInLabel: label, icon } = SOCIAL_PROVIDER_META[provider];
        const testID = `social-${provider}-${context}`;
        if (provider === 'google') {
          return (
            <GoogleSignInButton
              key={provider}
              label={label}
              disabled={disabled}
              loading={pending === provider}
              onPress={() => handlePress(provider)}
              testID={testID}
            />
          );
        }
        return (
          <Button
            key={provider}
            variant="outline"
            size="lg"
            fullWidth
            disabled={disabled}
            loading={pending === provider}
            onPress={() => handlePress(provider)}
            accessibilityLabel={label}
            testID={testID}
          >
            <View className="flex-row items-center gap-3">
              <Ionicons name={icon} size={20} color={theme.colors.text} />
              <Text className="text-base font-semibold text-foreground">{label}</Text>
            </View>
          </Button>
        );
      })}
    </View>
  );
}
